// src/shared/telemetry/telemetryUtils.ts
// ────────────────────────────────────────────────────────────────────────────
// DRY utility functions shared across all telemetry dashboards.
// Extracted from DaemonOpsPanel + IoTTelemetryPanel — single source of truth.
// ────────────────────────────────────────────────────────────────────────────

import type { TimeSeriesPoint } from '@/shared/components/plots/TelemetryTimeSeriesPlot'
import { fmtCompact as _fmtCompact } from '@/utils/format'

/* ── Signal Statistics ──────────────────────────────────────────────── */

export interface SignalStat {
  label: string
  color: string
  min: number
  max: number
  delta: number
  avg: number
  pts: number
}

export function computeStats(pts: TimeSeriesPoint[]): { min: number; max: number; delta: number; avg: number } | null {
  if (!pts || pts.length === 0) return null
  let min = Infinity, max = -Infinity, sum = 0
  for (const p of pts) {
    if (p.value < min) min = p.value
    if (p.value > max) max = p.value
    sum += p.value
  }
  return { min, max, delta: max - min, avg: sum / pts.length }
}

export function computeSignalStat(label: string, color: string, pts: TimeSeriesPoint[]): SignalStat | null {
  if (!pts.length) return null
  const s = computeStats(pts)
  if (!s) return null
  return { label, color, ...s, pts: pts.length }
}

/* ── Formatters ─────────────────────────────────────────────────────── */

export function fmtStat(v: number): string {
  return Math.abs(v) >= 100 ? String(Math.round(v)) : v.toFixed(1)
}

/** Compact number: 1.2K, 4.5M — delegates to @/utils/format (telemetry uses 1 decimal). */
export function fmtCompact(n: number): string {
  return _fmtCompact(n, 1)
}

export function pct(v: number | null, fallback = '—'): string {
  return v != null ? `${v.toFixed(1)}%` : fallback
}

export function formatUptime(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/** Human-friendly relative time ("5s ago", "3m ago", "1h 12m ago"). */
export function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 5) return 'just now'
  if (sec < 60) return `${Math.floor(sec)}s ago`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) {
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`
  }
  return `${Math.floor(sec / 86400)}d ago`
}

/** Full locale timestamp for hover tooltips. */
export function formatTsFull(v: string): string {
  try {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString()
  } catch { return v }
}

/** Format seconds into a human-friendly span ("2h 15m", "3d 4h"). */
export function fmtSpan(sec: number): string {
  if (sec < 60)     return `${sec}s`
  if (sec < 3600)   return `${Math.floor(sec / 60)}m ${sec % 60}s`
  if (sec < 86400)  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  return `${d}d ${h}h`
}
